import React, { useState, useRef, useEffect } from 'react';
import { SlideImage } from '../types';
import { slideApiService } from '../services/slideApiService';
import AuthenticatedImage from './AuthenticatedImage';
import ImageViewModal from './ImageViewModal';
import CropModal from './CropModal';

interface ImageManagerProps {
  shopId: string;
  slideId: string;
  images: SlideImage[];
  onImagesUpdate: (images: SlideImage[]) => void;
  onRotationsUpdate?: (rotations: {[imageId: string]: number}) => void;
  onCropsUpdate?: (crops: {[imageId: string]: { x: number; y: number; width: number; height: number }}) => void;
  // Phase 3b (optional): when provided, render a per-thumbnail publish click-badge + green
  // band reflecting the pending published set. Only passed from SlideEditModal (owner/admin);
  // Place/Location image managers are separate components and are unaffected.
  publishedImageIds?: Set<string>;
  onTogglePublished?: (imageId: string) => void;
  className?: string;
}

const MAX_IMAGES_PER_SLIDE = 10;

const ImageManager: React.FC<ImageManagerProps> = ({
  shopId,
  slideId,
  images,
  onImagesUpdate,
  onRotationsUpdate,
  onCropsUpdate,
  publishedImageIds,
  onTogglePublished,
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [localImages, setLocalImages] = useState<SlideImage[]>(images);
  const [imageRotations, setImageRotations] = useState<{[imageId: string]: number}>({});
  const [imageCrops, setImageCrops] = useState<{[imageId: string]: { x: number; y: number; width: number; height: number }}>({});
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [selectedImageDescription, setSelectedImageDescription] = useState<string | undefined>(undefined);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageId, setCropImageId] = useState<string | null>(null);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [loadingCropImage, setLoadingCropImage] = useState(false);

  // HEIC polling state
  const [heicPolling, setHeicPolling] = useState(false);

  // Camera state
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [cameraError, setCameraError] = useState('');

  // Camera refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update local images when prop changes
  useEffect(() => {
    setLocalImages(images);
  }, [images]);

  // HEIC polling: periodically re-fetch slide data to pick up new image ID after conversion
  useEffect(() => {
    if (!heicPolling) return;
    const interval = setInterval(async () => {
      try {
        const updated = await slideApiService.getSlideById(shopId, slideId);
        const oldIds = localImages.map(img => img.id).sort().join(',');
        const newIds = (updated.images || []).map((img: SlideImage) => img.id).sort().join(',');
        if (oldIds !== newIds) {
          setHeicPolling(false);
          setLocalImages(updated.images || []);
          onImagesUpdate(updated.images || []);
        }
      } catch {
        // Silently retry on next interval
      }
    }, 3000);
    const timeout = setTimeout(() => setHeicPolling(false), 120000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [heicPolling, slideId, localImages, onImagesUpdate]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Attach stream to video element when camera is active
  useEffect(() => {
    if (isCameraActive && cameraStream) {
      const timer = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = cameraStream;
          videoRef.current.play().catch(err => {
            console.error('Failed to play video:', err);
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isCameraActive, cameraStream]);

  // Sort images by display order
  const sortedImages = [...localImages].sort((a, b) => a.displayOrder - b.displayOrder);

  // Total count includes both uploaded images and pending files
  const totalImageCount = sortedImages.length + pendingFiles.length;
  const atMaxCapacity = totalImageCount >= MAX_IMAGES_PER_SLIDE;

  // Camera functions
  const startCamera = async () => {
    try {
      setCameraError('');
      let mediaStream: MediaStream | null = null;

      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          }
        });
      } catch (mobileErr) {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true
        });
      }

      if (!mediaStream) {
        throw new Error('Failed to acquire camera stream');
      }

      setCameraStream(mediaStream);
      setIsCameraActive(true);
    } catch (err) {
      setCameraError('Failed to access camera. Please check permissions or use "Photos".');
      console.error('Camera error:', err);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setPendingFiles(prev => [...prev, file]);
      }
    }, 'image/jpeg', 0.92);
  };

  const handleCloseCamera = () => {
    stopCamera();
    if (pendingFiles.length > 0) {
      uploadPendingFiles();
    }
  };

  // Upload pending files to server
  const uploadPendingFiles = async () => {
    if (pendingFiles.length === 0) return;

    const filesToUpload = [...pendingFiles];
    setPendingFiles([]);
    setUploading(true);

    try {
      let currentImages = [...sortedImages];
      for (const file of filesToUpload) {
        if (currentImages.length >= MAX_IMAGES_PER_SLIDE) break;
        try {
          const uploadedImage = await slideApiService.uploadImage(shopId, slideId, file);
          currentImages = [...currentImages, uploadedImage];
        } catch (error) {
          console.error('Error uploading image:', error);
        }
      }
      setLocalImages(currentImages);
      onImagesUpdate(currentImages);
    } finally {
      setUploading(false);
    }
  };

  // File input handler (Photos button)
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remaining = MAX_IMAGES_PER_SLIDE - totalImageCount;
    const filesToAdd = files.slice(0, remaining);

    if (filesToAdd.length > 0) {
      setPendingFiles(prev => [...prev, ...filesToAdd]);
      // Upload immediately since user is done selecting
      uploadPendingFilesFromList([...pendingFiles, ...filesToAdd]);
    }

    // Reset input so same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Upload a specific list of files (used by file input handler)
  const uploadPendingFilesFromList = async (files: File[]) => {
    if (files.length === 0) return;

    setPendingFiles([]);
    setUploading(true);

    try {
      let currentImages = [...sortedImages];
      for (const file of files) {
        if (currentImages.length >= MAX_IMAGES_PER_SLIDE) break;
        try {
          const uploadedImage = await slideApiService.uploadImage(shopId, slideId, file);
          currentImages = [...currentImages, uploadedImage];
        } catch (error) {
          console.error('Error uploading image:', error);
        }
      }
      setLocalImages(currentImages);
      onImagesUpdate(currentImages);
    } finally {
      setUploading(false);
    }
  };

  const removePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteImage = (imageId: string) => {
    const updatedImages = localImages.filter(img => img.id !== imageId);
    const reorderedImages = updatedImages.map((img, index) => ({
      ...img,
      displayOrder: index
    }));
    setLocalImages(reorderedImages);
    onImagesUpdate(reorderedImages);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newImages = [...sortedImages];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    const reorderedImages = newImages.map((img, index) => ({
      ...img,
      displayOrder: index
    }));

    setLocalImages(reorderedImages);
    onImagesUpdate(reorderedImages);
    setDraggedIndex(null);
  };

  const handleRotateLeft = (imageId: string) => {
    const newRotations = {
      ...imageRotations,
      [imageId]: ((imageRotations[imageId] || 0) - 90) % 360
    };
    setImageRotations(newRotations);
    if (onRotationsUpdate) {
      onRotationsUpdate(newRotations);
    }
  };

  const handleRotateRight = (imageId: string) => {
    const newRotations = {
      ...imageRotations,
      [imageId]: ((imageRotations[imageId] || 0) + 90) % 360
    };
    setImageRotations(newRotations);
    if (onRotationsUpdate) {
      onRotationsUpdate(newRotations);
    }
  };

  const handleCropClick = async (imageId: string) => {
    try {
      setLoadingCropImage(true);
      setCropImageId(imageId);

      if (cropImageUrl) {
        URL.revokeObjectURL(cropImageUrl);
        setCropImageUrl(null);
      }

      const response = await slideApiService.fetchAuthenticatedImage(shopId, slideId, imageId);

      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      setCropImageUrl(objectUrl);
      setCropModalOpen(true);
    } catch (error) {
      console.error('Error loading image for crop:', error);
      alert(`Failed to load image for cropping. ${error instanceof Error ? error.message : 'Please try again.'}`);
      setCropImageId(null);
      setCropModalOpen(false);
    } finally {
      setLoadingCropImage(false);
    }
  };

  const handleCropComplete = (cropArea: { x: number; y: number; width: number; height: number }) => {
    if (cropImageId) {
      const newCrops = {
        ...imageCrops,
        [cropImageId]: cropArea
      };
      setImageCrops(newCrops);
      if (onCropsUpdate) {
        onCropsUpdate(newCrops);
      }
    }
    setCropModalOpen(false);
    setCropImageId(null);

    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
      setCropImageUrl(null);
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setCropImageId(null);

    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
      setCropImageUrl(null);
    }
  };

  const handleImageClick = (image: SlideImage) => {
    setSelectedImageId(image.id);
    setSelectedImageDescription(image.description);
  };

  const handleImageNavigation = (imageId: string) => {
    const image = sortedImages.find(img => img.id === imageId);
    if (image) {
      setSelectedImageId(imageId);
      setSelectedImageDescription(image.description);
    }
  };

  return (
    <div className={`image-manager ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Images ({sortedImages.length}/{MAX_IMAGES_PER_SLIDE})
      </label>

      {/* Max capacity warning */}
      {atMaxCapacity && (
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '1px solid var(--border-light)',
          borderRadius: '4px',
          padding: '8px 12px',
          marginBottom: '12px',
          fontSize: '0.875rem',
          color: 'var(--text-secondary)'
        }}>
          Maximum of {MAX_IMAGES_PER_SLIDE} images per slide reached. Delete an image to upload more.
        </div>
      )}

      {/* Camera / Photos Buttons */}
      {!isCameraActive && !atMaxCapacity && (
        <div style={{ marginBottom: '16px' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            gap: '8px',
            alignItems: 'center'
          }}>
            <button
              type="button"
              onClick={startCamera}
              disabled={uploading}
              style={{
                width: '100%',
                backgroundColor: uploading ? 'var(--disabled-bg)' : 'var(--primary-dark)',
                color: uploading ? 'var(--disabled)' : 'var(--text-inverse)',
                padding: '10px 8px',
                fontSize: '0.8rem',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              Camera
            </button>

            <div style={{
              color: 'var(--text-secondary)',
              fontSize: '0.75rem',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}>OR</div>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{
                width: '100%',
                backgroundColor: uploading ? 'var(--disabled-bg)' : 'var(--border-light)',
                color: uploading ? 'var(--disabled)' : 'var(--text-primary)',
                padding: '10px 8px',
                fontSize: '0.8rem',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              Photos
            </button>
          </div>
          {cameraError && (
            <div style={{ color: 'var(--error-text)', fontSize: '0.75rem', marginTop: '8px' }}>
              {cameraError}
            </div>
          )}
        </div>
      )}

      {/* Uploading indicator */}
      {uploading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '4px',
          marginBottom: '12px',
          fontSize: '0.875rem',
          color: 'var(--text-secondary)'
        }}>
          <div className="spinner-sm"></div>
          Uploading images...
        </div>
      )}

      {/* Desktop Camera UI (inline) */}
      {isCameraActive && !isMobile && (
        <div style={{
          position: 'relative',
          backgroundColor: 'var(--photo-backdrop)',
          borderRadius: '8px',
          overflow: 'visible',
          marginBottom: '16px',
          width: '100%',
          maxWidth: '640px'
        }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: 'auto',
              minHeight: '300px',
              maxHeight: '400px',
              objectFit: 'cover',
              display: 'block',
              backgroundColor: 'var(--photo-backdrop)'
            }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {/* Photo counter */}
          <div style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 500
          }}>
            {pendingFiles.length} captured
          </div>

          {/* Close button */}
          <button
            type="button"
            onClick={handleCloseCamera}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              fontSize: '1.25rem',
              cursor: 'pointer',
              zIndex: 10
            }}
          >
            ✕
          </button>

          {/* Capture button */}
          <button
            type="button"
            onClick={capturePhoto}
            disabled={totalImageCount + pendingFiles.length >= MAX_IMAGES_PER_SLIDE}
            style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'var(--primary)',
              color: 'var(--text-inverse)',
              padding: '12px 24px',
              fontSize: '1.125rem',
              borderRadius: '50%',
              border: '4px solid white',
              width: '60px',
              height: '60px',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            📷
          </button>
        </div>
      )}

      {/* Pending files preview */}
      {pendingFiles.length > 0 && !isCameraActive && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
            Uploading {pendingFiles.length} photo{pendingFiles.length > 1 ? 's' : ''}...
          </div>
        </div>
      )}

      {/* Pending files preview while camera is active */}
      {pendingFiles.length > 0 && isCameraActive && !isMobile && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
            Captured ({pendingFiles.length})
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {pendingFiles.map((file, index) => (
              <div
                key={index}
                style={{
                  width: '80px',
                  height: '60px',
                  position: 'relative',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  border: '2px dashed var(--border-light)'
                }}
              >
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Captured ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <button
                  type="button"
                  onClick={() => removePendingFile(index)}
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    fontSize: '0.65rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image Grid */}
      <div className="images-grid mb-4">
        {sortedImages.length > 0 ? (
          <div className="space-y-3 mb-4">
            {sortedImages.map((image, index) => (
              <div
                key={image.id}
                style={{
                  width: '150px',
                  height: '100px',
                  overflow: 'hidden',
                  display: 'inline-block',
                  verticalAlign: 'top',
                  flexShrink: 0,
                  position: 'relative',
                  border: '2px solid var(--border-light)',
                  borderRadius: '8px',
                  cursor: 'grab',
                  opacity: draggedIndex === index ? 0.5 : 1,
                  transition: 'all 0.15s ease',
                  marginRight: '8px',
                  marginBottom: '8px'
                }}
                draggable
                onDragStart={(e) => {
                  handleDragStart(e, index);
                  e.currentTarget.style.cursor = 'grabbing';
                }}
                onDragEnd={(e) => {
                  e.currentTarget.style.cursor = 'grab';
                }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                onMouseEnter={(e) => {
                  if (draggedIndex === null) {
                    e.currentTarget.style.borderColor = 'var(--primary-light)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(139, 69, 19, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (draggedIndex === null) {
                    e.currentTarget.style.borderColor = 'var(--border-light)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <div
                  onClick={(e) => {
                    if (!(e.target as HTMLElement).closest('button')) {
                      handleImageClick(image);
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer'
                  }}
                >
                  <AuthenticatedImage
                    shopId={shopId}
                    slideId={slideId}
                    imageId={image.id}
                    alt={image.description || `Image ${index + 1}`}
                    size="thumb_300"
                    onHeicPending={() => setHeicPolling(true)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      transform: `rotate(${imageRotations[image.id] || 0}deg)`,
                      transition: 'transform 0.3s ease'
                    }}
                  />
                </div>

                {/* Image Description Overlay */}
                {image.description && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      fontSize: '0.75rem',
                      padding: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {image.description}
                  </div>
                )}

                {/* Phase 3b: green band around published thumbnails (no band = shop-private).
                    Drawn as an inset ring so the hover border logic doesn't override it. */}
                {publishedImageIds?.has(image.id) && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '6px',
                    border: '3px solid var(--success)',
                    pointerEvents: 'none',
                    zIndex: 2
                  }} />
                )}

                {/* Phase 3b: always-visible publish click-badge (bottom-left). One click
                    toggles publish; the green band reflects state. Only rendered when the
                    parent supplies onTogglePublished (owner/admin via SlideEditModal). */}
                {onTogglePublished && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePublished(image.id); }}
                    title={publishedImageIds?.has(image.id)
                      ? 'Published - click to make shop-private'
                      : 'Not published - click to publish'}
                    aria-label={publishedImageIds?.has(image.id) ? 'Unpublish image' : 'Publish image'}
                    style={{
                      position: 'absolute',
                      bottom: '4px',
                      left: '4px',
                      zIndex: 3,
                      backgroundColor: publishedImageIds?.has(image.id) ? 'var(--success)' : 'rgba(0, 0, 0, 0.6)',
                      color: 'var(--text-inverse)',
                      borderRadius: '4px',
                      height: '22px',
                      padding: '0 7px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      border: 'none',
                      cursor: 'pointer',
                      opacity: 0.95,
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.95'; }}
                  >
                    {publishedImageIds?.has(image.id) ? '● Published' : '○ Publish'}
                  </button>
                )}

                {/* Rotation Buttons */}
                <div style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  display: 'flex',
                  gap: '4px'
                }}>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRotateLeft(image.id); }}
                    style={{
                      backgroundColor: 'rgba(0, 123, 255, 0.9)', color: 'white', borderRadius: '4px',
                      width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.75rem', fontWeight: 'bold', border: 'none', cursor: 'pointer',
                      transition: 'opacity 0.15s ease', opacity: 0.8
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.8'; }}
                    title="Rotate left 90°" aria-label="Rotate left"
                  >↶</button>

                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRotateRight(image.id); }}
                    style={{
                      backgroundColor: 'rgba(0, 123, 255, 0.9)', color: 'white', borderRadius: '4px',
                      width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.75rem', fontWeight: 'bold', border: 'none', cursor: 'pointer',
                      transition: 'opacity 0.15s ease', opacity: 0.8
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.8'; }}
                    title="Rotate right 90°" aria-label="Rotate right"
                  >↷</button>

                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleCropClick(image.id); }}
                    style={{
                      backgroundColor: 'rgba(0, 123, 255, 0.9)', color: 'white', borderRadius: '4px',
                      width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.75rem', fontWeight: 'bold', border: 'none', cursor: 'pointer',
                      transition: 'opacity 0.15s ease', opacity: 0.8
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.8'; }}
                    title="Crop image" aria-label="Crop image"
                  >✂</button>

                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteImage(image.id); }}
                    style={{
                      backgroundColor: 'var(--error)', color: 'var(--text-inverse)', borderRadius: '4px',
                      width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.875rem', fontWeight: 'bold', border: 'none', cursor: 'pointer',
                      transition: 'opacity 0.15s ease', opacity: 0.8
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.backgroundColor = 'var(--error)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.8'; e.currentTarget.style.backgroundColor = 'var(--error)'; }}
                    aria-label="Delete image"
                  >×</button>
                </div>

                {/* Drag Handle */}
                <div
                  style={{
                    position: 'absolute', top: '4px', left: '4px',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)', color: 'white',
                    borderRadius: '4px', fontSize: '0.75rem', padding: '2px 4px',
                    opacity: 0.8, pointerEvents: 'none'
                  }}
                >
                  ⋮⋮
                </div>
              </div>
            ))}
          </div>
        ) : (
          !isCameraActive && pendingFiles.length === 0 && !uploading && (
            <div className="text-gray-500 text-sm italic border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              No images uploaded
            </div>
          )
        )}
      </div>

      {/* Instructions */}
      {sortedImages.length > 1 && (
        <div className="mt-3 text-xs text-gray-500">
          Drag and drop images to reorder them
        </div>
      )}

      {/* Image View Modal */}
      {selectedImageId && (
        <ImageViewModal
          shopId={shopId}
          slideId={slideId}
          imageId={selectedImageId}
          imageDescription={selectedImageDescription}
          allImages={sortedImages}
          onNavigate={handleImageNavigation}
          onClose={() => {
            setSelectedImageId(null);
            setSelectedImageDescription(undefined);
          }}
        />
      )}

      {/* Crop Modal */}
      {cropModalOpen && cropImageId && cropImageUrl && (() => {
        const image = sortedImages.find(img => img.id === cropImageId);
        if (!image) return null;

        return (
          <CropModal
            imageUrl={cropImageUrl}
            imageName={image.description || image.filename || 'Image'}
            onCropComplete={handleCropComplete}
            onCancel={handleCropCancel}
          />
        );
      })()}

      {/* Full-screen mobile camera overlay */}
      {/* visibility:visible ensures this overlay is shown even when a parent has visibility:hidden */}
      {isCameraActive && isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'var(--photo-backdrop)',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            visibility: 'visible'
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              zIndex: 10
            }}
          >
            <div style={{ fontSize: '14px', fontWeight: 500 }}>
              {pendingFiles.length} captured
            </div>
            <button
              onClick={handleCloseCamera}
              type="button"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: 'white',
                cursor: 'pointer',
                padding: '0'
              }}
            >
              ✕
            </button>
          </div>

          {/* Camera viewport */}
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                backgroundColor: 'var(--photo-backdrop)'
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Pending thumbnails strip */}
            {pendingFiles.length > 0 && (
              <div style={{
                position: 'absolute',
                bottom: '160px',
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                gap: '6px',
                padding: '0 16px'
              }}>
                {pendingFiles.slice(-5).map((file, index) => (
                  <div
                    key={index}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      border: '2px solid white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                    }}
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Captured ${index + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Capture button */}
            <button
              type="button"
              onClick={capturePhoto}
              disabled={totalImageCount + pendingFiles.length >= MAX_IMAGES_PER_SLIDE}
              style={{
                position: 'absolute',
                bottom: '80px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'var(--primary)',
                color: 'var(--text-inverse)',
                fontSize: '1.125rem',
                borderRadius: '50%',
                border: '4px solid white',
                width: '70px',
                height: '70px',
                fontWeight: 600,
                cursor: totalImageCount + pendingFiles.length >= MAX_IMAGES_PER_SLIDE ? 'not-allowed' : 'pointer',
                opacity: totalImageCount + pendingFiles.length >= MAX_IMAGES_PER_SLIDE ? 0.5 : 1,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              📷
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageManager;
